import qs from "query-string";
import { Context } from "urql";

import { Logger } from "@lululemon/mwa-logger";
import { publicConfig } from "@lululemon/ecom-core";

import {
  RECOMMENDER_BASE,
  REC_QUERY,
} from "@lululemon/ecom-common/src/graphql/graphQLComponentQueries";

const DEFAULT_DATA_SOURCE = "datascience";

export const recsErrorLogger = new Logger({
  owner: "navrec",
  app: "recommendations-app",
});

function adaptGQLSKUStyleOrder(skuStyleOrder) {
  for (const item of skuStyleOrder) {
    Object.assign(item, {
      "color-group": item.colorGroup || item["color-group"],
      "color-id": item.colorId || item["color-id"],
      "color-name": item.colorName || item["color-name"],
      "sku-style-order-id": item.skuStyleOrderId || item["sku-style-order-id"],
      "style-id": item.styleId || item["style-id"],
      "style-id-01": item.styleId01 || item["style-id-01"],
      "style-id-02": item.styleId02 || item["style-id-02"],
    });
  }
}

export function adaptGQLResponse(data) {
  for (const product of data?.included?.[0]?.items || []) {
    adaptGQLSKUStyleOrder(product?.["sku-style-order"] || []);
  }
  return data;
}

const parseRecsLegacyData = (data, queryLabel) => {
  const {
    [queryLabel]: { legacyJSON },
  } = data;
  return adaptGQLResponse(JSON.parse(legacyJSON));
};

export const setItemToItemsQueryParams = ({
  locale,
  productId,
  itemCount = 8,
  modelId,
  dataSource = DEFAULT_DATA_SOURCE,
  stockLevel = 0,
  fusionExperimentVariant,
}) => {
  return {
    locale,
    itemId: productId,
    itemCount,
    dataSource,
    modelId,
    stockLevel,
    fusionExperimentVariant,
  };
};

export const setUserToItemsQueryParams = ({
  locale,
  userId,
  itemCount = 8,
  modelId,
  fallback,
  dataSource = DEFAULT_DATA_SOURCE,
}) => {
  return {
    locale,
    userId,
    itemCount,
    dataSource,
    modelId,
    fallback,
  };
};

export const fetchGraphQLRecommenders = async (
  queryLabel = "",
  graphqlClient,
  queryParams,
  errorHandling = {
    message: "Error fetching recommendations",
    tags: {
      componentName: "ProductDetailPageContainer",
      functionName: "setProductRecommendations",
    },
    onError: () => {},
    errorLogger: recsErrorLogger,
  }
) => {
  const apiHost = `${publicConfig?.apiHost}`;
  const KIWI_PATH = "/snb/graphql";
  Context.url = `${apiHost}${KIWI_PATH}`;
  const query = `${RECOMMENDER_BASE(queryLabel)}${REC_QUERY()}`;

  const { data, error } = await graphqlClient
    .query(query, { ...queryParams }, Context)
    .toPromise();

  let parsingError = null;
  if (!error) {
    try {
      return parseRecsLegacyData(data, queryLabel);
    } catch (err) {
      parsingError = err;
    }
  }
  if (error || parsingError) {
    errorHandling?.errorLogger?.error(
      error,
      {
        tags: errorHandling.tags,
        apiContext: {
          statusCode: error?.response?.status,
          url: Context.url,
          method: "POST",
          service: "snb/graphql",
        },
      },
      (scope) => {
        if (typeof window !== "undefined") {
          scope.setContext("params", qs.parse(location?.search || {}));
        }
        scope.setTag("errorMessage", errorHandling.message);
        scope.setTag(
          "graphqlErrorMessage",
          error?.message || parsingError?.message
        );
        scope.setTag("graphqlErrors", error?.graphQLErrors);
        scope.setTag("itemsType", queryLabel);
        scope.setTag("queryParams", queryParams);
        scope.setTag(
          "errorType",
          parsingError ? "Data parsing error" : "Service error"
        );
      }
    );
    errorHandling?.onError?.(error || parsingError);
  }
};
